<template>
  <div class="bg-gray-200 min-vh-100 d-flex align-items-center" :dir="isAr ? 'rtl' : 'ltr'">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-4 col-md-8 col-12" style="margin-top: 3rem">
          <div class="card z-index-0 fadeIn3 fadeInBottom" style="overflow: visible">
            <div class="p-0 position-relative mt-n4 mx-3 z-index-2" style="margin-top: -50px; overflow: visible">
              <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
                <h4 class="text-white font-weight-bolder text-center mt-2 mb-0">
                  {{ $t("login.title") }}
                </h4>

                <div class="row mt-3">
                  <div class="col-12 text-center">
                    <i class="material-icons opacity-10 text-white" style="font-size: 3rem">lock</i>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-body">
              <form @submit.prevent="login" novalidate>
                <div class="form-group my-4">
                  <div class="input-container">
                    <div class="input-group input-group-dynamic" :class="isAr ? 'icon-at-start' : 'icon-at-end'">
                      <input
                        type="email"
                        class="form-control"
                        v-model="form.email"
                        required
                        autocomplete="username email"
                        @focus="focusField('email')"
                        @blur="blurField('email')"
                        :class="isAr ? 'input-with-icon-left' : 'input-with-icon-right'"
                        placeholder=" "
                      />
                      <label class="form-label" :class="{ active: isEmailActive || form.email }">
                        {{ $t("login.email") }}
                      </label>
                      <span class="input-group-text" :class="isAr ? 'icon-left' : 'icon-right'">
                        <i class="material-icons">email</i>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="form-group mb-4">
                  <div class="input-container">
                    <div class="input-group input-group-dynamic" :class="isAr ? 'icon-at-start' : 'icon-at-end'">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        class="form-control"
                        v-model="form.password"
                        required
                        autocomplete="current-password"
                        @focus="focusField('password')"
                        @blur="blurField('password')"
                        :class="isAr ? 'input-with-icon-left' : 'input-with-icon-right'"
                        placeholder=" "
                      />
                      <label class="form-label" :class="{ active: isPasswordActive || form.password }">
                        {{ $t("login.password") }}
                      </label>
                      <span
                        class="input-group-text cursor-pointer"
                        :class="isAr ? 'icon-left' : 'icon-right'"
                        @click="togglePasswordVisibility"
                        aria-label="toggle password visibility"
                        role="button"
                      >
                        <i class="material-icons">{{ showPassword ? "visibility" : "visibility_off" }}</i>
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="showSchoolSelector" class="form-group mb-4">
                  <div class="input-container">
                    <div class="input-group input-group-dynamic">
                      <select class="form-control" v-model="form.tenant_slug" required>
                        <option value="" disabled>Select School</option>
                        <option v-for="school in availableSchools" :key="school.slug" :value="school.slug">
                          {{ school.name }}
                        </option>
                      </select>
                      <label class="form-label" :class="{ active: form.tenant_slug }">School</label>
                    </div>
                  </div>
                </div>

                <div class="text-center">
                  <button
                    type="submit"
                    class="btn bg-secondary w-100 my-4 mb-2 position-relative overflow-hidden transition-all login-btn"
                    :disabled="isLoading"
                  >
                    <span
                      class="btn-content position-relative z-index-1 d-inline-flex align-items-center justify-content-center w-100"
                      :class="{ 'content-hidden': isLoading }"
                    >
                      <span class="btn-inner--text text-white">{{ $t("login.button") }}</span>
                      <span class="btn-inner--icon text-white">
                        <i class="material-icons">login</i>
                      </span>
                    </span>

                    <span v-if="isLoading" class="spinner-center" role="status" aria-live="polite">
                      <span class="spinner-border text-white login-spinner" role="status"></span>
                    </span>

                    <span class="hover-effect position-absolute w-100 h-100 bg-white opacity-10 top-0 start-0"></span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div class="legal-links mt-3 text-center">
            <button class="legal-link-btn" type="button" @click="goLegal('privacy')">
              {{ $t("legal.privacy") }}
            </button>
            <span class="legal-sep">•</span>
            <button class="legal-link-btn" type="button" @click="goLegal('terms')">
              {{ $t("legal.terms") }}
            </button>
            <span class="legal-sep">•</span>
            <button class="legal-link-btn" type="button" @click="goLegal('contact')">
              {{ $t("legal.contact") }}
            </button>
          </div>

          <div class="legal-note mt-2 text-center">
            <small class="text-muted">{{ $t("legal.note") }}</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import api from "@/api";

const { t, locale } = useI18n();
const isAr = computed(() => locale.value === "ar");

const router = useRouter();
const store = useStore();
const toast = useToast();

const form = ref({ email: "", password: "", tenant_slug: "" });

const isEmailActive = ref(false);
const isPasswordActive = ref(false);
const showPassword = ref(false);
const isLoading = ref(false);

const availableSchools = ref([]);
const showSchoolSelector = ref(false);

const goLegal = (type) => {
  if (type === "privacy") return router.push({ name: "legal.privacy" });
  if (type === "terms") return router.push({ name: "legal.terms" });
  return router.push({ name: "legal.contact" });
};

onMounted(async () => {
  if (showSchoolSelector.value) {
    try {
      const response = await api.get("/tenants");
      availableSchools.value = response.data;
    } catch (error) {
      console.error("Failed to fetch schools:", error);
    }
  }
});
const getErrorMessage = (error) => {
  const msg = error?.response?.data?.message;

  if (typeof msg === "object" && msg !== null) {
    return locale.value === "ar" ? msg.ar || msg.en : msg.en || msg.ar;
  }

  if (typeof msg === "string") {
    return msg;
  }

  return t("login.failed");
};
const login = async () => {
  isLoading.value = true;
  try {
    const credentials = {
      email: form.value.email,
      password: form.value.password,
    };

    if (showSchoolSelector.value && form.value.tenant_slug) {
      credentials.tenant_slug = form.value.tenant_slug;
    }

    const res = await store.dispatch("loginUser", credentials);

    toast.success(t("login.success"));
    router.push(res?.redirect_to || "/");
  } catch (error) {
    toast.error(getErrorMessage(error));
    console.error("Login error:", error);
  } finally {
    isLoading.value = false;
  }
};

const focusField = (field) => {
  if (field === "email") isEmailActive.value = true;
  if (field === "password") isPasswordActive.value = true;
};

const blurField = (field) => {
  if (field === "email") isEmailActive.value = false;
  if (field === "password") isPasswordActive.value = false;
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700&display=swap");

:root { --primary:#2c91ae; --primary-dark:#1e657a; --secondary:#8f32d6; --secondary-dark:#7326b8; }

.bg-gradient-primary { background: linear-gradient(195deg, #2c91ae 0%, #1e657a 100%); }
.shadow-primary {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14),
              0 7px 10px -5px rgba(44, 145, 174, 0.4) !important;
}
.border-radius-lg { border-radius: 0.75rem; }
.position-relative { position: relative; }
.mt-n4 { margin-top: -1.5rem; }
.z-index-2 { z-index: 2; }

.bg-gray-200{
  font-family: "Tajawal", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
  background:
    radial-gradient(900px 500px at 15% 10%, rgba(44,145,174,0.18), transparent 60%),
    radial-gradient(700px 420px at 85% 20%, rgba(143,50,214,0.14), transparent 60%),
    linear-gradient(180deg, #eef2f7 0%, #e9eef5 100%) !important;
}

.container{ padding-left: 1rem; padding-right: 1rem; }

.card{
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 1rem;
  box-shadow: 0 18px 50px rgba(2, 8, 23, 0.10);
  background: #fff;
}

.card-body{ padding: 1.4rem 1.25rem 1.25rem; }

.bg-gradient-primary{
  border-radius: 0.9rem;
  position: relative;
  overflow: hidden;
}

.bg-gradient-primary::after{
  content:"";
  position:absolute;
  inset:-2px;
  background:
    radial-gradient(220px 140px at 18% 25%, rgba(255,255,255,0.35), transparent 60%),
    radial-gradient(260px 160px at 78% 35%, rgba(255,255,255,0.22), transparent 62%);
  pointer-events:none;
}

h4{
  letter-spacing: .2px;
  text-shadow: 0 8px 18px rgba(0,0,0,0.20);
}

.bg-gradient-primary .material-icons{
  filter: drop-shadow(0 10px 14px rgba(0,0,0,0.25));
}

.btn.bg-secondary{
  background: linear-gradient(195deg, #8f32d6 0%, #7326b8 100%) !important;
  border: none !important;
  border-radius: 0.85rem;
  padding: 0.95rem 1rem;
  box-shadow: 0 14px 30px rgba(143,50,214,0.22);
  transition: transform .22s ease, box-shadow .22s ease, filter .22s ease;
  color: #fff !important;
}

.login-btn{
  height: 52px;
  min-height: 52px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.login-spinner{
  width: 0.85rem;
  height: 0.85rem;
  border-width: 0.12em;
}
.btn.bg-secondary:hover{
  transform: translateY(-2px);
  box-shadow: 0 18px 40px rgba(143,50,214,0.28);
  filter: brightness(1.02);
}
.btn.bg-secondary:active{ transform: translateY(0); }

.btn-inner--text{ font-weight: 700; letter-spacing: .2px; }
.btn-inner--icon .material-icons{ font-size: 1.15rem; margin-inline-start: .25rem; }

.hover-effect{ opacity: 0; transition: opacity .25s ease; }
.btn:hover .hover-effect{ opacity: 0.12; }

.fadeIn3 { animation: fadeIn 0.35s ease-out; }
.fadeInBottom { animation: fadeInBottom 0.55s cubic-bezier(0.2, 0.9, 0.2, 1); }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes fadeInBottom { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }

@media (max-width: 576px){
  .card-body{ padding: 1.25rem 1.05rem 1.05rem; }
}

.input-container { position: relative; padding-top: 8px; }

.input-group-dynamic {
  position: relative;
  display: flex;
  align-items: stretch;
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  background-color: #fff;
  flex-direction: row;
}
.input-group-dynamic:hover { border-color: var(--primary); }
.input-group-dynamic:focus-within { border-color: var(--primary); box-shadow: 0 0 0 2px rgba(44,145,174,0.2); }

.input-group-dynamic .form-control {
  flex: 1 1 auto;
  width: 1%;
  min-width: 0;
  border: none !important;
  background-color: transparent;
  height: auto;
  box-shadow: none !important;
  order: 1;
}
.input-group-dynamic .form-control:focus { outline: none; }

.input-with-icon-right { padding: 0.85rem 2.75rem 0.85rem 0.85rem !important; }
.input-with-icon-left { padding: 0.85rem 0.85rem 0.85rem 2.75rem !important; }

.input-group-dynamic .form-label,
.input-group-dynamic label {
  position: absolute !important;
  top: 0.86rem !important;
  left: 0.9rem !important;
  transform-origin: top left !important;
  background: #fff !important;
  padding: 0 6px !important;
  color: #6c757d !important;
  font-weight: 500;
  line-height: 1;
  pointer-events: none;
  transition: transform .18s ease, color .18s ease, opacity .18s ease;
  z-index: 2;
  max-width: calc(100% - 3.2rem);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

[dir="rtl"] .input-group-dynamic .form-label,
[dir="rtl"] .input-group-dynamic label {
  left: auto !important;
  right: 0.9rem !important;
  transform-origin: top right !important;
}

.input-group-dynamic.icon-at-start .form-label,
.input-group-dynamic.icon-at-start label {
  left: calc(0.9rem + 2.75rem) !important;
  max-width: calc(100% - 2.75rem - 1.4rem);
}

[dir="rtl"] .input-group-dynamic.icon-at-start .form-label,
[dir="rtl"] .input-group-dynamic.icon-at-start label {
  left: auto !important;
  right: calc(0.9rem + 2.75rem) !important;
}

.input-group-dynamic .form-control:focus + label,
.input-group-dynamic .form-control:not(:placeholder-shown) + label,
.input-group-dynamic label.active {
  transform: translateY(-1.15rem) scale(0.82) !important;
  color: var(--primary) !important; opacity: .95;
}

.input-group-dynamic .form-control::placeholder { opacity: 0; }

.input-group-dynamic .input-group-text {
  min-width: 2.75rem;
  display: flex; align-items: center; justify-content: center;
  padding: 0.75rem;
  color: #6c757d; background-color: transparent;
  border: none !important;
  flex: 0 0 auto;
  z-index: 3;
}

.input-group-dynamic .input-group-text.icon-right {
  order: 2;
  border-left: 1px solid #ced4da !important;
  border-right: none !important;
}
.input-group-dynamic .input-group-text.icon-left {
  order: 0;
  border-right: 1px solid #ced4da !important;
  border-left: none !important;
}

[dir="rtl"] .input-group-dynamic .input-group-text.icon-right {
  border-left: none !important;
  border-right: 1px solid #ced4da !important;
}
[dir="rtl"] .input-group-dynamic .input-group-text.icon-left {
  border-right: none !important;
  border-left: 1px solid #ced4da !important;
}

.cursor-pointer { cursor: pointer; }

.btn-content.content-hidden{ opacity: 0; pointer-events: none; }
.spinner-center{
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.legal-links{
  display:flex;
  gap:.6rem;
  justify-content:center;
  align-items:center;
  flex-wrap:wrap;
}
.legal-sep{ color: rgba(15, 23, 42, 0.35); font-weight: 800; }
.legal-note small{ display:inline-block; max-width: 420px; line-height: 1.5; }

.legal-link-btn{
  background: none;
  border: none;
  padding: 0;
  font-weight: 800;
  font-size: .9rem;
  color: #1e657a;
  cursor: pointer;
  transition: opacity .2s ease, transform .2s ease;
}
.legal-link-btn:hover{
  opacity: .85;
  transform: translateY(-1px);
  text-decoration: underline;
}
</style>